import { isPwaStandalone } from './fullscreenPreference'
import { isConstrainedUiDevice } from './mobileDownloadProfile'

const STORAGE_KEY = 'udv.pwaInstallDismissedUntil'
const RESUME_VIEWER_URL_KEY = 'udv.pwaResumeViewerUrl'
const DISMISS_DAYS = 7

function canUseStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage
  } catch (e) {
    return false
  }
}

/**
 * @returns {number} epoch ms hasta el cual el usuario pidió no ver el CTA
 */
export function getPwaInstallDismissedUntil() {
  if (!canUseStorage()) return 0
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const n = raw != null ? Number(raw) : 0
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch (e) {
    return 0
  }
}

export function isPwaInstallDismissed() {
  const until = getPwaInstallDismissedUntil()
  return until > Date.now()
}

/**
 * Oculta el CTA de instalación por unos días.
 * @param {number} [days]
 */
export function dismissPwaInstallPrompt(days = DISMISS_DAYS) {
  if (!canUseStorage()) return
  const d = Number(days)
  const span = (Number.isFinite(d) && d > 0 ? d : DISMISS_DAYS) * 86400000
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now() + span))
  } catch (e) {
    /* ignore */
  }
}

/**
 * ¿Mostrar CTA instalar PWA? (navegador, no dismiss reciente).
 * @param {{ pwaInstallAllowed?: boolean }} [opts]
 * @returns {boolean}
 */
export function shouldOfferPwaInstall(opts = {}) {
  if (opts && opts.pwaInstallAllowed === false) return false
  if (isPwaStandalone()) return false
  if (isPwaInstallDismissed()) return false
  return true
}

/** PWA instalable desde el navegador (ignora dismiss del banner). */
export function canInstallPwaInBrowser() {
  return !isPwaStandalone()
}

/** Móvil / tablet / viewport compacto (mismo criterio que UI edge). */
export function isPwaMobileLikeDevice() {
  return isConstrainedUiDevice()
}

/**
 * CTA principal de instalación.
 * @param {(key: string) => string} t
 */
export function getPwaInstallLabel(t) {
  return t('pwa.install')
}

/**
 * Banner superior (portal puede añadir contexto de guardar estudios).
 * @param {(key: string) => string} t
 * @param {{ portal?: boolean }} [opts]
 */
export function getPwaInstallBannerLabel(t, opts = {}) {
  if (opts.portal) {
    return t(
      isPwaMobileLikeDevice()
        ? 'pwa.installBannerPortalMobile'
        : 'pwa.installBannerPortalDesktop'
    )
  }
  return getPwaInstallLabel(t)
}

/**
 * @param {(key: string) => string} t
 */
export function getPwaInstallShareHint(t) {
  return t(
    isPwaMobileLikeDevice()
      ? 'pwa.installShareHintMobile'
      : 'pwa.installShareHintDesktop'
  )
}

/**
 * @param {(key: string) => string} t
 */
export function getPwaInstallSuccessBody(t) {
  return t(
    isPwaMobileLikeDevice()
      ? 'pwa.installSuccessBodyMobile'
      : 'pwa.installSuccessBodyDesktop'
  )
}

/**
 * @param {(key: string) => string} t
 */
export function getPwaInstallSuccessIconHint(t) {
  return t(
    isPwaMobileLikeDevice()
      ? 'pwa.installSuccessIconHintMobile'
      : 'pwa.installSuccessIconHintDesktop'
  )
}

/**
 * Guarda URL del estudio para reanudar tras instalar / primer arranque PWA.
 * @param {string} url
 */
export function savePwaResumeViewerUrl(url) {
  if (!canUseStorage()) return
  const value = String(url || '').trim()
  if (!value) return
  try {
    window.localStorage.setItem(RESUME_VIEWER_URL_KEY, value)
  } catch (e) {
    /* ignore */
  }
}

/**
 * @returns {string|null}
 */
export function getPwaResumeViewerUrl() {
  if (!canUseStorage()) return null
  try {
    const raw = window.localStorage.getItem(RESUME_VIEWER_URL_KEY)
    const value = raw != null ? String(raw).trim() : ''
    return value || null
  } catch (e) {
    return null
  }
}

export function clearPwaResumeViewerUrl() {
  if (!canUseStorage()) return
  try {
    window.localStorage.removeItem(RESUME_VIEWER_URL_KEY)
  } catch (e) {
    /* ignore */
  }
}

/**
 * Primer arranque standalone: redirige al estudio guardado al instalar.
 * @returns {boolean} true si se disparó redirect
 */
export function maybeRedirectPwaResumeViewerUrl() {
  if (!isPwaStandalone() || typeof window === 'undefined') return false
  const saved = getPwaResumeViewerUrl()
  if (!saved) return false
  try {
    const cur = new URL(window.location.href)
    const tgt = new URL(saved, window.location.origin)
    const hasDeepLink =
      tgt.searchParams.has('t') ||
      tgt.searchParams.has('manifestUrl') ||
      tgt.searchParams.has('udvInvite')
    if (!hasDeepLink) return false
    if (cur.pathname === tgt.pathname && cur.search === tgt.search) {
      clearPwaResumeViewerUrl()
      return false
    }
    clearPwaResumeViewerUrl()
    window.location.replace(saved)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Vuelve al portal sin duplicar pestaña: cierra tab si el portal abrió UDV (opener),
 * o history.back si mismo origen; si no, navega al portal.
 * @param {string|null|undefined} portalUrl
 * @returns {'close'|'back'|'navigate'|'noop'}
 */
export function returnToPortalFromViewerTab(portalUrl) {
  if (typeof window === 'undefined') return 'noop'
  const portal = portalUrl != null ? String(portalUrl).trim() : ''

  if (window.opener) {
    try {
      if (!window.opener.closed) {
        window.opener.focus()
      }
    } catch (e) {
      /* cross-origin focus */
    }
    window.close()
    return 'close'
  }

  try {
    const ref = document.referrer
    if (ref && portal) {
      const refOrigin = new URL(ref).origin
      const portalOrigin = new URL(portal).origin
      if (refOrigin === portalOrigin && window.history.length > 1) {
        window.history.back()
        return 'back'
      }
    }
  } catch (e) {
    /* ignore */
  }

  if (portal && /^https?:\/\//i.test(portal)) {
    window.location.href = portal
    return 'navigate'
  }
  return 'noop'
}
